import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Inbox, Send, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { AdminGate, ADMIN_KEY, AdminSwitch } from "@/components/AdminGate";

const FILTERS = [["new", "New"], ["answered", "Answered"], ["", "All"]];
const fmt = (d) => new Date(d).toLocaleString();

function MessageCard({ m, adminKey, onSaved }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const headers = { "X-Admin-Key": adminKey };

  const send = async () => {
    if (!reply.trim()) return toast.error("Please write a reply first.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/contact/${m.id}/reply`, { body: reply }, { headers });
      toast.success(`Reply sent to ${m.email}`);
      setReply("");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the reply.");
    } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    const { data } = await api.patch(`/admin/contact/${m.id}`, { status }, { headers });
    onSaved(data);
  };

  return (
    <div data-testid={`contact-card-${m.id}`} className="bg-card border border-border rounded-2xl p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-serif text-2xl font-600 text-ink">{m.name}</h3>
        <span data-testid={`contact-status-${m.id}`} className={`text-xs font-700 rounded-full px-3 py-1 ${m.status === "answered" ? "bg-olive/15 text-olive" : "bg-gold-light text-ink"}`}>
          {m.status === "answered" ? "Answered" : "New"}
        </span>
        <span className="text-xs font-mono text-ink/45 ml-auto">{fmt(m.created_at)}</span>
      </div>
      <p className="text-sm text-olive mt-1">{m.email}</p>
      <p className="mt-3 text-ink/80 whitespace-pre-line leading-relaxed">{m.message}</p>

      {m.replies?.length > 0 && (
        <div className="mt-4 space-y-2">
          {m.replies.map((r, i) => (
            <div key={r.sent_at} data-testid={`contact-reply-${m.id}-${i}`} className="border-l-2 border-gold pl-4 py-1">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Your reply · {fmt(r.sent_at)}</p>
              <p className="text-sm text-ink/75 whitespace-pre-line mt-1">{r.body}</p>
            </div>
          ))}
        </div>
      )}

      <Textarea data-testid={`contact-reply-input-${m.id}`} value={reply} onChange={(e) => setReply(e.target.value)}
        placeholder={`Write your reply to ${m.name}…`} className="mt-4 bg-parchment min-h-[100px]" maxLength={6000} />
      <div className="flex flex-wrap gap-2 mt-3">
        <Button data-testid={`contact-reply-send-${m.id}`} disabled={busy} onClick={send}
          className="rounded-full bg-gold text-ink hover:bg-ink hover:text-parchment font-700">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send Reply
        </Button>
        {m.status === "new" ? (
          <Button variant="outline" data-testid={`contact-mark-answered-${m.id}`} onClick={() => setStatus("answered")} className="rounded-full">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Mark answered
          </Button>
        ) : (
          <Button variant="outline" data-testid={`contact-mark-new-${m.id}`} onClick={() => setStatus("new")} className="rounded-full">
            <RotateCcw className="w-4 h-4 mr-2" /> Move back to New
          </Button>
        )}
      </div>
      <p className="text-[11px] text-ink/45 mt-2">Sent from Brother's Immanuel; their reply comes to info@brothersimmanuel.ca.</p>
    </div>
  );
}

export default function ContactInbox() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(ADMIN_KEY) || "");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("new");

  useEffect(() => {
    if (!adminKey) return;
    api.get("/admin/contact", { params: { status: filter }, headers: { "X-Admin-Key": adminKey } })
      .then(({ data }) => setItems(data))
      .catch(() => { sessionStorage.removeItem(ADMIN_KEY); setAdminKey(""); });
  }, [adminKey, filter]);

  const onSaved = (d) => setItems((xs) => (filter && d.status !== filter ? xs.filter((x) => x.id !== d.id) : xs.map((x) => (x.id === d.id ? d : x))));

  return (
    <div data-testid="contact-inbox-page" className="max-w-4xl mx-auto px-5 sm:px-8 pt-14 pb-16">
      {adminKey && <AdminSwitch />}
      <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink mb-8 flex items-center gap-3"><Inbox className="w-9 h-9 text-gold" /> Contact Messages</h1>
      {!adminKey ? <AdminGate onUnlock={setAdminKey} /> : (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {FILTERS.map(([k, label]) => (
              <button key={k || "all"} data-testid={`contact-filter-${k || "all"}`} onClick={() => setFilter(k)}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === k ? "bg-ink text-parchment border-ink" : "bg-card border-border text-ink/70 hover:border-gold"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="space-y-4" data-testid="contact-list">
            {items.length === 0 && <p data-testid="contact-list-empty" className="text-ink/55">No messages here.</p>}
            {items.map((m) => <MessageCard key={m.id} m={m} adminKey={adminKey} onSaved={onSaved} />)}
          </div>
        </>
      )}
    </div>
  );
}
